import '../scss/parts/mobile-navigation.scss';

const root = document.querySelector('[data-mobile-navigation]');
if (root && !root.dataset.initialized) {
    // Some templates wrap the footer. Keep the dialog outside the page we make inert.
    document.body.append(root);
    root.dataset.initialized = 'true';
    const config = JSON.parse(root.dataset.config);
    const panel = root.querySelector('.mobile-nav__panel');
    const share = root.querySelector('.mobile-nav__share');
    const shade = root.querySelector('.mobile-nav__shade');
    const list = root.querySelector('.mobile-nav__list');
    const title = root.querySelector('.mobile-nav__title');
    const back = root.querySelector('[data-mobile-back]');
    const search = root.querySelector('.mobile-nav__search');
    const contacts = root.querySelector('.mobile-nav__contacts');
    const mobile = window.matchMedia('(max-width: 991.98px)');
    const stack = [];
    let opened = null;
    let previousFocus = null;
    let background = [];
    let navigating = false;
    let transitionId = 0;
    const transitions = new Set();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    async function navigate(direction, update) {
        if (navigating || opened !== 'menu') return;
        navigating = true;
        const id = ++transitionId;
        const height = panel.getBoundingClientRect().height;
        const offset = direction === 'back' ? -18 : 18;
        const animate = (element, frames, duration) => {
            const animation = element.animate(frames, {
                duration,
                easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
            });
            transitions.add(animation);
            return animation.finished.catch(() => {}).finally(() => transitions.delete(animation));
        };
        try {
            if (!reducedMotion.matches) {
                await Promise.all(
                    [list, title]
                        .filter((element) => !element.hidden)
                        .map((element) =>
                            animate(
                                element,
                                [
                                    { opacity: 1, transform: 'translateX(0)' },
                                    { opacity: 0, transform: `translateX(${-offset}px)` },
                                ],
                                80,
                            ),
                        ),
                );
            }
            // Closing or switching panels must invalidate the pending navigation.
            if (id !== transitionId || opened !== 'menu') return;
            update();
            render();
            (stack.length ? back : list.querySelector('button, a'))?.focus({ preventScroll: true });
            if (!reducedMotion.matches) {
                const nextHeight = panel.getBoundingClientRect().height;
                await Promise.all([
                    animate(panel, [{ height: `${height}px` }, { height: `${nextHeight}px` }], 200),
                    ...[list, title]
                        .filter((element) => !element.hidden)
                        .map((element) =>
                            animate(
                                element,
                                [
                                    { opacity: 0, transform: `translateX(${offset}px)` },
                                    { opacity: 1, transform: 'translateX(0)' },
                                ],
                                200,
                            ),
                        ),
                ]);
            }
        } finally {
            if (id === transitionId) navigating = false;
        }
    }

    function children(item) {
        return Object.values(item.children || {});
    }

    function render() {
        const page = stack.at(-1);
        list.replaceChildren();
        title.hidden = !page;
        title.textContent = page?.title || '';
        back.hidden = !page;
        search.hidden = !!page;
        contacts.hidden = !!page;
        const items = page?.items || config.items;
        const append = (item, isParent = false) => {
            const nested = children(item);
            const expandable = nested.length > 0;
            const row = document.createElement(expandable || item.current ? 'button' : 'a');
            row.className = 'mobile-nav__row';
            if (row.tagName === 'BUTTON') row.type = 'button';
            else row.href = item.href;
            const icon = (name, className) => {
                const element = document.createElement('span');
                element.className = className;
                element.setAttribute('aria-hidden', 'true');
                element.style.setProperty('--icon', `url("${config.iconBase}${name}.svg")`);
                return element;
            };
            if (page && expandable) {
                row.classList.add('has-folder');
                row.append(icon('folder', 'mobile-nav__folder'));
            }
            const text = document.createElement('span');
            text.className = 'mobile-nav__row-label';
            text.textContent = isParent ? config.labels.all : item.title;
            row.append(text);
            if (item.current) {
                row.disabled = true;
                row.classList.add('is-current');
            }
            if (expandable || item.current || page?.isLanguage) {
                const arrow = icon(item.current ? 'check' : 'chevron', 'mobile-nav__arrow');
                row.append(arrow);
            }
            if (expandable) {
                row.addEventListener('click', () => {
                    navigate('forward', () => {
                        stack.push({ title: item.title, items: nested, parent: item });
                    });
                });
            }
            list.append(row);
        };
        if (page?.parent?.href && !['#', ''].includes(page.parent.href)) {
            append({ title: config.labels.all, href: page.parent.href }, true);
        }
        items.forEach((item) => append(item));
        if (!page && config.languages.length) {
            const language = document.createElement('button');
            language.type = 'button';
            language.className = 'mobile-nav__row';
            const label = document.createElement('span');
            label.textContent = config.labels.language;
            const current = document.createElement('span');
            current.className = 'mobile-nav__language';
            current.textContent = config.languages.find((item) => item.current)?.title || '';
            const arrow = document.createElement('span');
            arrow.className = 'mobile-nav__arrow';
            arrow.setAttribute('aria-hidden', 'true');
            arrow.style.setProperty('--icon', `url("${config.iconBase}chevron.svg")`);
            language.append(label, current, arrow);
            language.addEventListener('click', () => {
                navigate('forward', () => {
                    stack.push({
                        title: config.labels.language,
                        items: config.languages,
                        isLanguage: true,
                    });
                });
            });
            list.append(language);
        }
        list.scrollTop = 0;
    }

    function close(restore = true) {
        transitionId++;
        navigating = false;
        transitions.forEach((animation) => animation.cancel());
        transitions.clear();
        opened = null;
        panel.hidden = share.hidden = shade.hidden = true;
        document.body.classList.remove('mobile-nav-open');
        background.forEach(([element, wasInert]) => (element.inert = wasInert));
        background = [];
        root.querySelectorAll('[aria-expanded]').forEach((button) => {
            button.setAttribute('aria-expanded', 'false');
            button.classList.remove('is-active');
        });
        if (restore) previousFocus?.focus();
    }

    function toggle(mode, button) {
        if (opened === mode) return close();
        close(false);
        previousFocus = button;
        opened = mode;
        stack.length = 0;
        render();
        panel.hidden = mode !== 'menu';
        share.hidden = mode !== 'share';
        shade.hidden = false;
        document.body.classList.add('mobile-nav-open');
        background = [...document.body.children]
            .filter(
                (element) =>
                    !element.contains(root) &&
                    !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName),
            )
            .map((element) => [element, element.inert]);
        background.forEach(([element]) => (element.inert = true));
        button.classList.add('is-active');
        button.setAttribute('aria-expanded', 'true');
        const target = mode === 'menu' ? panel : share;
        target.querySelector('button:not([hidden]), a')?.focus();
    }

    root.querySelectorAll('[data-mobile-action]').forEach((button) => {
        const action = button.dataset.mobileAction;
        const goals = {
            home: 'coins',
            exchanges: 'exchanges',
            share: 'share',
            up: 'up',
            menu: 'menu_main',
        };
        if (button.tagName === 'A' && new URL(button.href).pathname === location.pathname) {
            button.classList.add('is-current');
            button.setAttribute('aria-current', 'page');
        }
        button.addEventListener('click', () => {
            window.cryptoReachGoal?.(`menu_mob_${goals[action]}`);
            if (action === 'menu' || action === 'share') toggle(action, button);
            if (action === 'up') {
                close(false);
                window.scrollTo({
                    top: 0,
                    behavior: matchMedia('(prefers-reduced-motion: reduce)').matches
                        ? 'instant'
                        : 'smooth',
                });
            }
        });
    });
    root.querySelectorAll('[data-mobile-close]').forEach((button) =>
        button.addEventListener('click', () => close()),
    );
    back.addEventListener('click', () => {
        navigate('back', () => stack.pop());
    });
    root.querySelectorAll('[data-mobile-share]').forEach((link) => {
        link.addEventListener('click', () => {
            const url = encodeURIComponent(location.href);
            const text = encodeURIComponent(document.title);
            link.href = {
                telegram: `https://t.me/share/url?url=${url}&text=${text}`,
                vk: `https://vk.com/share.php?url=${url}&title=${text}`,
                twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
            }[link.dataset.mobileShare];
        });
    });
    root.querySelector('[data-mobile-copy]').addEventListener('click', async () => {
        const status = root.querySelector('[role="status"]');
        try {
            await navigator.clipboard.writeText(location.href);
            status.textContent = config.labels.copied;
        } catch {
            status.textContent = config.labels.copyError;
        }
    });
    document.addEventListener('keydown', (event) => {
        if (!opened) return;
        if (event.key === 'Escape') close();
        if (event.key === 'Tab') {
            const focusable = [...root.querySelectorAll('a, button, input')].filter(
                (element) => !element.disabled && element.getClientRects().length,
            );
            const first = focusable[0];
            const last = focusable.at(-1);
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            }
            if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    });
    mobile.addEventListener('change', () => close(false));
    window.addEventListener('pagehide', () => close(false));
    function viewport() {
        const visual = window.visualViewport;
        const keyboard = visual && window.innerHeight - visual.height > 150;
        root.style.setProperty(
            '--mobile-nav-keyboard',
            keyboard ? `${window.innerHeight - visual.height - visual.offsetTop}px` : '0px',
        );
        root.classList.toggle('has-keyboard', !!keyboard);
    }
    window.visualViewport?.addEventListener('resize', viewport);
    viewport();
}
